'use client'

import { usePathname } from 'next/navigation'
import { useRef } from 'react'

export type NavSection = {
  readonly href: string
  readonly label: string
}

/**
 * The header's section links, with a pill that slides to whichever one is hovered or
 * focused. Fragments (`#trust`) on the homepage, where they scroll; `/#trust` on every
 * other page, where a bare fragment would have nothing to land on.
 */
export function HeaderNav({
  sections,
}: {
  readonly sections: readonly NavSection[]
}) {
  const list = useRef<HTMLUListElement>(null)
  const pathname = usePathname()
  const onHomepage = pathname === '/' || pathname === null
  const resolve = (href: string) =>
    href.startsWith('#') && !onHomepage ? `/${href}` : href

  const show = (link: HTMLElement) => {
    const el = list.current
    if (!el) return
    el.style.setProperty('--nav-x', `${link.offsetLeft}px`)
    el.style.setProperty('--nav-w', `${link.offsetWidth}px`)
    // Flush layout before the live class turns the transitions on, so the first pill
    // appears in place rather than sliding in from the left edge.
    void el.offsetWidth
    el.classList.add('nav-hover-live')
  }

  const hide = () => list.current?.classList.remove('nav-hover-live')

  return (
    <ul
      className="nav-hover relative flex items-center gap-1"
      onBlur={(event) => {
        if (!list.current?.contains(event.relatedTarget as Node | null)) hide()
      }}
      onPointerLeave={hide}
      ref={list}
    >
      <span aria-hidden="true" className="nav-hover-pill" />
      {sections.map((section) => (
        <li key={section.href}>
          <a
            className="relative z-10 block rounded-full px-3 py-2 text-sm text-ink-muted transition-colors hover:text-ink focus-visible:text-ink"
            href={resolve(section.href)}
            onFocus={(event) => show(event.currentTarget)}
            onPointerEnter={(event) => show(event.currentTarget)}
          >
            {section.label}
          </a>
        </li>
      ))}
    </ul>
  )
}
